import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Calculate distance between two coordinates using Haversine formula
export function calculateDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) *
      Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // Distance in km
  return distance;
}

function deg2rad(deg: number): number {
  return deg * (Math.PI / 180);
}

// Get user's current position
export function getCurrentPosition(): Promise<{ lat: number; lng: number }> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by your browser"));
    } else {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          reject(error);
        }
      );
    }
  });
}

// Format price range
export function formatPriceRange(priceRange: number): string {
  return "€".repeat(priceRange);
}

// Format date
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('de-DE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

// Parse URL search params into SearchFilters object
export function parseSearchParams(searchParams: URLSearchParams) {
  return {
    query: searchParams.get('q') || undefined,
    location: searchParams.get('location') || undefined,
    cuisineIds: searchParams.get('cuisines')?.split(',').map(Number) || undefined,
    priceRange: searchParams.get('price')?.split(',').map(Number) || undefined,
    minRating: searchParams.get('rating') ? parseFloat(searchParams.get('rating')!) : undefined,
    maxDistance: searchParams.get('distance') ? parseFloat(searchParams.get('distance')!) : undefined,
  };
}

// Debounce function to limit how often a function is called
export function debounce<F extends (...args: any[]) => any>(
  func: F,
  waitFor: number
): (...args: Parameters<F>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<F>): void => {
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => func(...args), waitFor);
  };
}

/**
 * Normalize image path for display
 * - If path starts with '/' or 'http', return as-is
 * - Otherwise prefix with '/images/' for legacy paths
 */
export function normalizeImagePath(path: string | undefined | null): string {
  if (!path) return '';
  if (path.startsWith('/') || path.startsWith('http')) return path;
  return `/images/${path}`;
}
